import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  InputAdornment,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import HealthAndSafetyOutlinedIcon from "@mui/icons-material/HealthAndSafetyOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import ChecklistRtlOutlinedIcon from "@mui/icons-material/ChecklistRtlOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import SafetyCheckOutlinedIcon from "@mui/icons-material/SafetyCheckOutlined";
import BiotechOutlinedIcon from "@mui/icons-material/BiotechOutlined";
import ForestOutlinedIcon from "@mui/icons-material/ForestOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import AirlineSeatFlatOutlinedIcon from "@mui/icons-material/AirlineSeatFlatOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import { DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED } from "./theme";
import { useColorMode } from "../../contexts/ColorModeContext";

const ACCENT = "#E8A317";
const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
const DURATION = "280ms";

const ICON_BY_LABEL = {
  "AUDITORIA INTERNA": HealthAndSafetyOutlinedIcon,
  "ATENDIMENTO AMBULAT.": LocalHospitalOutlinedIcon,
  "CHECK-LIST": ChecklistRtlOutlinedIcon,
  CIPA: GroupsOutlinedIcon,
  DDS: CampaignOutlinedIcon,
  DESVIOS: ReportProblemOutlinedIcon,
  "EPI-CONTROLE": SafetyCheckOutlinedIcon,
  "INSPEÇÃO DE SST": BiotechOutlinedIcon,
  "INSPEÇÃO AMBIENTAL": ForestOutlinedIcon,
  "PAINEL DE CONTROLE": DashboardCustomizeOutlinedIcon,
  RAC: AssignmentLateOutlinedIcon,
  TREINAMENTOS: SchoolOutlinedIcon,
  AFASTAMENTOS: AirlineSeatFlatOutlinedIcon,
  ATENDIMENTOS: SupportAgentOutlinedIcon,
  "CONTROLE DE EXPERIÊNCIA": HourglassTopOutlinedIcon,
  "EXAMES OCUPACIONAIS": MonitorHeartOutlinedIcon,
  "GER. FOLGAS": BeachAccessOutlinedIcon,
  "FOLHA DE PAGAMENTO": PaymentsOutlinedIcon,
  "PROCESSO ADM/TRANSF": SwapHorizOutlinedIcon,
  REPUBLICAS: HomeWorkOutlinedIcon,
  "GERAR RELATÓRIO": DescriptionOutlinedIcon,
  "GERAR CONTRATOS": DrawOutlinedIcon,
  "ORGANOGRAMA COLABS": AccountTreeOutlinedIcon,
  "CALIB. EQUIPAMENTOS": PrecisionManufacturingOutlinedIcon,
};

const BrandMark = ({ onClick }) => (
  <Tooltip title="Voltar aos módulos" placement="right">
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label="Ir para módulos"
      sx={{
        width: 40,
        height: 40,
        p: 0,
        border: "none",
        borderRadius: "50%",
        cursor: "pointer",
        flexShrink: 0,
        overflow: "hidden",
        bgcolor: ACCENT,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: `0 6px 16px ${alpha(ACCENT, 0.35)}`,
        transition: "transform 0.2s ease",
        "&:hover": { transform: "scale(1.05)" },
        "&:active": { transform: "scale(0.96)" },
        "& img": {
          width: 56,
          height: 56,
          objectFit: "cover",
          objectPosition: "left center",
          display: "block",
          pointerEvents: "none",
        },
      }}
    >
      <img src="/Images/Logo.png" alt="Logo Palmont" />
    </Box>
  </Tooltip>
);

const NavItem = ({ item, expanded, onActivate }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const Icon = ICON_BY_LABEL[item.label] || BoltOutlinedIcon;
  const muted = isDark ? alpha("#fff", 0.55) : alpha("#152A36", 0.55);
  const text = isDark ? alpha("#fff", 0.9) : "#152A36";
  const hoverBg = isDark ? alpha("#fff", 0.06) : alpha("#152A36", 0.05);

  return (
    <Tooltip title={!expanded ? item.label : ""} placement="right" enterDelay={350}>
      <ListItemButton
        onClick={() => onActivate(item)}
        disabled={Boolean(item.isLoading)}
        sx={{
          mx: expanded ? 1 : 0.75,
          px: expanded ? 1.25 : 1,
          py: 0.95,
          minHeight: 42,
          justifyContent: expanded ? "flex-start" : "center",
          borderRadius: 2.5,
          color: text,
          "&:hover": { bgcolor: hoverBg },
          "& .MuiListItemIcon-root": {
            color: muted,
            minWidth: expanded ? 34 : 0,
            justifyContent: "center",
          },
        }}
      >
        <ListItemIcon>
          {item.isLoading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            <Icon sx={{ fontSize: 20 }} />
          )}
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontSize: 13,
            fontWeight: 600,
            noWrap: true,
          }}
          sx={{
            opacity: expanded ? 1 : 0,
            maxWidth: expanded ? 190 : 0,
            m: 0,
            overflow: "hidden",
            whiteSpace: "nowrap",
            transition: `opacity 180ms ${EASE}, max-width ${DURATION} ${EASE}`,
          }}
        />
      </ListItemButton>
    </Tooltip>
  );
};

const DrawerChrome = ({
  modulo,
  items,
  expanded,
  pinnedCollapsed,
  onToggleCollapse,
  onActivate,
  showCollapseToggle = true,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { mode, toggleColorMode } = useColorMode();
  const isDark = theme.palette.mode === "dark";
  const [query, setQuery] = useState("");

  const border = isDark ? alpha("#fff", 0.08) : alpha("#152A36", 0.08);
  const muted = isDark ? alpha("#fff", 0.5) : alpha("#152A36", 0.5);
  const text = isDark ? "#fff" : "#152A36";

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => item.label.toLowerCase().includes(q));
  }, [items, query]);

  const rotinas = filtered.filter((item) => item.path || item.external);
  const acoes = filtered.filter((item) => item.action && !item.path && !item.external);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "space-between" : "center",
          px: expanded ? 2 : 1,
          pt: 2,
          pb: 1.5,
          gap: 1.25,
          minHeight: 68,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, flex: 1 }}>
          <BrandMark onClick={() => navigate("/modules")} />
          <Box
            sx={{
              opacity: expanded ? 1 : 0,
              maxWidth: expanded ? 150 : 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
              transition: `opacity 180ms ${EASE}, max-width ${DURATION} ${EASE}`,
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: 15, color: text, lineHeight: 1.15 }}>
              {String(modulo || "").toUpperCase()}
            </Typography>
            <Typography sx={{ fontSize: 11, color: muted, fontWeight: 500 }}>
              Home V2 · preview
            </Typography>
          </Box>
        </Box>

        {showCollapseToggle && (
          <Tooltip
            title={pinnedCollapsed ? "Manter expandido" : "Recolher menu"}
            placement="right"
          >
            <IconButton
              onClick={onToggleCollapse}
              size="small"
              sx={{
                color: muted,
                bgcolor: isDark ? alpha("#fff", 0.06) : alpha("#152A36", 0.05),
                opacity: expanded ? 1 : 0,
                pointerEvents: expanded ? "auto" : "none",
              }}
            >
              {pinnedCollapsed ? (
                <ChevronRightIcon fontSize="small" />
              ) : (
                <ChevronLeftIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box
        sx={{
          px: expanded ? 2 : 1,
          pb: 1.25,
          opacity: expanded ? 1 : 0,
          maxHeight: expanded ? 56 : 0,
          overflow: "hidden",
        }}
      >
        <TextField
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar rotina"
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchOutlinedIcon sx={{ fontSize: 18, color: muted }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5,
              bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#152A36", 0.04),
              fontSize: 13.5,
              "& fieldset": { borderColor: border },
              "&:hover fieldset": { borderColor: alpha(ACCENT, 0.45) },
              "&.Mui-focused fieldset": { borderColor: ACCENT },
            },
          }}
        />
      </Box>

      <Box
        sx={{
          px: 0.5,
          pb: 1.5,
          overflowY: "auto",
          overflowX: "hidden",
          flex: 1,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: 8,
            bgcolor: isDark ? alpha("#fff", 0.15) : alpha("#152A36", 0.15),
          },
        }}
      >
        {expanded && rotinas.length > 0 && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              px: 2,
              pt: 0.5,
              pb: 0.75,
              color: muted,
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              fontSize: 10.5,
            }}
          >
            Rotinas
          </Typography>
        )}
        {rotinas.map((item) => (
          <NavItem
            key={`${item.label}-${item.path || item.external}`}
            item={item}
            expanded={expanded}
            onActivate={onActivate}
          />
        ))}

        {expanded && acoes.length > 0 && (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              px: 2,
              pt: 1.25,
              pb: 0.75,
              color: muted,
              fontWeight: 700,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              fontSize: 10.5,
            }}
          >
            Ações
          </Typography>
        )}
        {acoes.map((item) => (
          <NavItem
            key={`acao-${item.label}`}
            item={item}
            expanded={expanded}
            onActivate={onActivate}
          />
        ))}

        {filtered.length === 0 && expanded && (
          <Typography sx={{ px: 2.5, py: 2, color: muted, fontSize: 13 }}>
            Nenhuma rotina encontrada.
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          borderTop: `1px solid ${border}`,
          px: expanded ? 1.5 : 0.75,
          py: 1.25,
          display: "flex",
          flexDirection: expanded ? "row" : "column",
          alignItems: "center",
          justifyContent: expanded ? "space-between" : "center",
          gap: 0.75,
        }}
      >
        <Tooltip
          title={mode === "dark" ? "Ativar modo claro" : "Ativar modo noturno"}
          placement="right"
        >
          <IconButton
            size="small"
            onClick={toggleColorMode}
            aria-label="Alternar tema"
            sx={{
              color: muted,
              bgcolor: isDark ? alpha("#fff", 0.06) : alpha("#152A36", 0.05),
              border: `1px solid ${border}`,
              "&:hover": {
                color: ACCENT,
                bgcolor: alpha(ACCENT, 0.12),
                borderColor: alpha(ACCENT, 0.35),
              },
            }}
          >
            {mode === "dark" ? (
              <LightModeOutlinedIcon fontSize="small" />
            ) : (
              <DarkModeOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        {expanded && (
          <Typography variant="caption" sx={{ color: muted, fontWeight: 600 }}>
            {mode === "dark" ? "Modo noturno" : "Modo claro"}
          </Typography>
        )}
      </Box>
    </>
  );
};

const HomeV2Nav = ({
  modulo,
  items,
  expanded,
  pinnedCollapsed,
  onToggleCollapse,
  onHoverChange,
  mobileOpen,
  onMobileClose,
  onActivate,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const width = expanded ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED;
  const paperSx = {
    boxSizing: "border-box",
    color: isDark ? "#fff" : "#152A36",
    bgcolor: isDark ? "#0E1620" : "#FFFFFF",
    borderRight: `1px solid ${isDark ? alpha("#fff", 0.08) : alpha("#152A36", 0.08)}`,
    display: "flex",
    flexDirection: "column",
    backgroundImage: "none",
  };

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { ...paperSx, width: DRAWER_WIDTH },
        }}
      >
        <DrawerChrome
          modulo={modulo}
          items={items}
          expanded
          pinnedCollapsed={false}
          onToggleCollapse={() => {}}
          onActivate={(item) => {
            onActivate(item);
            onMobileClose?.();
          }}
          showCollapseToggle={false}
        />
      </Drawer>

      <Drawer
        variant="permanent"
        onMouseEnter={() => onHoverChange?.(true)}
        onMouseLeave={() => onHoverChange?.(false)}
        sx={{
          display: { xs: "none", md: "block" },
          width,
          flexShrink: 0,
          whiteSpace: "nowrap",
          transition: `width ${DURATION} ${EASE}`,
          "& .MuiDrawer-paper": {
            ...paperSx,
            width,
            position: "relative",
            overflowX: "hidden",
            transition: `width ${DURATION} ${EASE}`,
          },
        }}
      >
        <DrawerChrome
          modulo={modulo}
          items={items}
          expanded={expanded}
          pinnedCollapsed={pinnedCollapsed}
          onToggleCollapse={onToggleCollapse}
          onActivate={onActivate}
        />
      </Drawer>
    </>
  );
};

export default HomeV2Nav;
